<template>
  <div class="search-origin">
    <Input
      id="originStation"
      :label="$t('travelPlanner.search.origin')"
      :internalLabel="true"
      v-model="inputValue"
      :hasDelete="true"
      :placeholder="$t('travelPlanner.search.origin')"
      :icon="compass"
      :suggestions="filteredStations"
      @focus="onInputFocus"
      @select-suggestion="handleSelectSuggestion" />
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed, watch } from 'vue'
  import { useTravelSearchStore } from '@/stores/travelSearchStore'
  import { fetchStations } from '@/services/journeyService'
  import { pushInteractionToDatalayer } from '@/services/gaLoggingService'
  import { Station } from '@/types/stationTypes'
  import Input from '@/components/BaseComponents/Input.vue'
  import compass from '@/assets/icons/compass.svg'
  import { getSortedStationSuggestions, SortableStation } from '@/utils/stationSortingUtils'
  import { getSearchHistory, addToSearchHistory } from '@/composables/useSearchHistory'

  export default defineComponent({
    name: 'SearchOrigin',
    components: {
      Input,
    },
    setup() {
      const travelSearchStore = useTravelSearchStore()
      const stations = computed(() => travelSearchStore.allStations)
      const selectedStation = ref<Station | null>(null)
      const inputValue = ref('')

      const onInputFocus = async () => {
        try {
          // Only fetch stations if not already loaded
          if (!stations.value.length) {
            const fetchedStations = await fetchStations()
            travelSearchStore.setAllStations(fetchedStations)
          }
        } catch (error) {
          console.error('Error fetching stations:', error)
        }
      }

      const handleSelectSuggestion = (stationCode: string) => {
        const station = stations.value.find((s) => s._u_i_c_station_code === stationCode)
        const name = station?.name ?? getSearchHistory('originSearchHistory').find((h) => h.code === stationCode)?.name
        if (name) {
          travelSearchStore.setOriginStation({ name, _u_i_c_station_code: stationCode })
          travelSearchStore.setOriginDestination(name, travelSearchStore.destination)
          selectedStation.value = station ?? null
          inputValue.value = name
          addToSearchHistory('originSearchHistory', { name, code: stationCode })
          pushInteractionToDatalayer('add origin', { selection: name })
        }
      }

      const filteredStations = computed(() => {
        if (!inputValue.value) return getSearchHistory('originSearchHistory')
        const stationsToFilter: SortableStation[] = stations.value.map((station) => ({
          name: station.name,
          _u_i_c_station_code: station._u_i_c_station_code,
          district: station.district,
          county: station.county,
        }))
        return getSortedStationSuggestions(stationsToFilter, inputValue.value)
      })

      watch(
        () => travelSearchStore.OriginStation,
        (newOriginStation) => {
          inputValue.value = newOriginStation.name
          travelSearchStore.setOriginDestination(newOriginStation.name, travelSearchStore.destination)
        },
        { immediate: true },
      )

      watch(
        () => inputValue.value,
        (newValue) => {
          if (!newValue) {
            travelSearchStore.setOriginStation({
              name: '',
              _u_i_c_station_code: '',
            })
            travelSearchStore.setOriginDestination('', travelSearchStore.destination)
            selectedStation.value = null
          }
        },
      )

      return {
        compass,
        stations,
        selectedStation,
        inputValue,
        onInputFocus,
        handleSelectSuggestion,
        filteredStations,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .search-origin {
    .input {
      min-height: rem(64);
    }
  }
</style>
